import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, forwardRef, Stack, Text } from '@chakra-ui/react'
import { range } from 'lodash'

import { ReminderMode, ReminderTrigger } from '~shared/types'

import { FieldColorScheme } from '~theme/foundations/colours'
import { Calendar } from '~components/Calendar'
import { SingleSelect } from '~components/Dropdown'
import { ReminderFieldSchema, ReminderFieldValue } from '~templates/Field/types'

import { RecurringReminder } from './RecurringReminder'
import { createReminderObj } from './utils'

export interface ReminderProps {
  /**
   * Whether Reminder component is disabled.
   */
  isDisabled?: boolean
  /**
   * Function called once a reminder is updated
   * @param nextValue the value of the reminder
   */
  onChange?: (nextValue: ReminderFieldValue) => void
  /**
   * The value of the reminder to be `checked`
   * (in controlled mode)
   */
  value?: ReminderFieldValue
  /**
   * The value of the reminder to be `checked`
   * initially (in uncontrolled mode)
   */
  defaultValue?: ReminderFieldValue
  /**
   * The `name` attribute forwarded to each `reminder` element
   */
  name: string
  /**
   * schema of the component to render.
   */
  schema: ReminderFieldSchema
  /**
   * Color scheme of the component to render. Defaults to `primary`.
   */
  colorScheme?: FieldColorScheme
}

export const Reminder = forwardRef<ReminderProps, 'input'>(
  ({ schema, colorScheme, onChange, ...props }, ref) => {
    const initialFocusRef = useRef<HTMLInputElement>(null)
    const reminderOptions: ReminderFieldSchema['reminderOptions'] =
      schema.reminderOptions

    const [reminderTime, setReminderTime] = useState('00:00')
    const [startTriggerTime, setStartTriggerTime] = useState('00:00')
    const [endTriggerTime, setEndTriggerTime] = useState('00:00')
    const [startTriggerDate, setStartTriggerDate] = useState<Date | null>(null)
    const [endTriggerDate, setEndTriggerDate] = useState<Date | null>(null)
    const [selectedDay, setSelectedDay] = useState<string[]>([])

    const reminderValue = useMemo(
      () =>
        createReminderObj({
          reminderOptions,
          startTriggerDate,
          endTriggerDate,
          reminderTime,
          selectedDay,
        }),
      [
        endTriggerDate,
        reminderOptions,
        reminderTime,
        selectedDay,
        startTriggerDate,
      ],
    )

    useEffect(() => {
      onChange?.(reminderValue)
    }, [onChange, reminderValue])

    return (
      <>
        {reminderOptions.mode === ReminderMode.Recurring && (
          <>
            <RecurringReminder
              colorScheme={colorScheme}
              mode={reminderOptions.mode}
              frequency={reminderOptions.frequency}
              maxDaySelection={reminderOptions.maxDaySelection}
              selectedDay={selectedDay}
              setSelectedDay={setSelectedDay}
            />
            <Text fontSize="medium" pb={1}>
              Select Time
            </Text>
            <Stack direction="row" spacing={4} align="center" pb={4}>
              <SingleSelect
                name="hour"
                value={reminderTime.split(':')[0]}
                /**
                 * Set isInvalid to false because there will always be default value for hour
                 */
                isInvalid={false}
                isRequired={false}
                isClearable={false}
                placeholder="Select Hour"
                items={range(24).map((h) => String(h).padStart(2, '0'))}
                onChange={(data) =>
                  setReminderTime(`${data}:${reminderTime.split(':')[1]}`)
                }
              />
              <SingleSelect
                name="minute"
                value={reminderTime.split(':')[1]}
                /**
                 * Set isInvalid to false because there will always be default value for minute
                 */
                isInvalid={false}
                isRequired={false}
                isClearable={false}
                placeholder="Select Minute"
                items={range(60).map((m) => String(m).padStart(2, '0'))}
                onChange={(data) =>
                  setReminderTime(`${reminderTime.split(':')[0]}:${data}`)
                }
              />
            </Stack>
          </>
        )}
        {reminderOptions.startTrigger === ReminderTrigger.Exact && (
          <>
            <Text fontSize="medium" pb={1}>
              Start Trigger
            </Text>
            <Box
              as="div"
              bgColor={'theme-grey.100'}
              width={'fit-content'}
              p={1}
              pb={2}
              m={1}
            >
              <Calendar
                value={startTriggerDate}
                monthsToDisplay={1}
                colorScheme={colorScheme}
                onChange={(date) => setStartTriggerDate(date)}
                ref={initialFocusRef}
              />
            </Box>
            {!reminderOptions.startTriggerTime && (
              <>
                <Text fontSize="medium" pb={1}>
                  Select Time
                </Text>
                <Stack direction="row" spacing={4} align="center" pb={4}>
                  <SingleSelect
                    name="start-hour"
                    value={startTriggerTime.split(':')[0]}
                    isClearable={false}
                    placeholder="Select Hour"
                    items={range(24).map((h) => String(h).padStart(2, '0'))}
                    onChange={(data) => {
                      const newDate = startTriggerDate || new Date()
                      newDate.setHours(parseInt(data))

                      setStartTriggerDate(newDate)
                      setStartTriggerTime(
                        `${data}:${startTriggerTime.split(':')[1]}`,
                      )
                    }}
                  />
                  <SingleSelect
                    name="start-minute"
                    value={startTriggerTime.split(':')[1]}
                    isClearable={false}
                    placeholder="Select Minute"
                    items={range(60).map((m) => String(m).padStart(2, '0'))}
                    onChange={(data) => {
                      const newDate = startTriggerDate || new Date()
                      newDate.setMinutes(parseInt(data), 0)

                      setStartTriggerDate(newDate)
                      setStartTriggerTime(
                        `${startTriggerTime.split(':')[0]}:${data}`,
                      )
                    }}
                  />
                </Stack>
              </>
            )}
          </>
        )}
        {reminderOptions.endTrigger === ReminderTrigger.Exact && (
          <>
            <Text fontSize="medium" pb={1}>
              End Trigger
            </Text>
            <Box
              as="div"
              bgColor={'white'}
              width={'fit-content'}
              p={1}
              pb={2}
              m={1}
            >
              <Calendar
                value={endTriggerDate}
                monthsToDisplay={1}
                colorScheme={colorScheme}
                onChange={(date) => setEndTriggerDate(date)}
                ref={initialFocusRef}
              />
            </Box>
            {!reminderOptions.endTriggerTime && (
              <>
                <Text fontSize="medium" pb={1}>
                  Select Time
                </Text>
                <Stack direction="row" spacing={4} align="center" pb={4}>
                  <SingleSelect
                    name="end-hour"
                    value={endTriggerTime.split(':')[0]}
                    isClearable={false}
                    placeholder="Select Hour"
                    items={range(24).map((h) => String(h).padStart(2, '0'))}
                    onChange={(data) => {
                      const newDate = endTriggerDate || new Date()
                      newDate.setHours(parseInt(data))

                      setEndTriggerDate(newDate)
                      setEndTriggerTime(
                        `${data}:${endTriggerTime.split(':')[1]}`,
                      )
                    }}
                  />
                  <SingleSelect
                    name="end-minute"
                    value={endTriggerTime.split(':')[1]}
                    isClearable={false}
                    placeholder="Select Minute"
                    items={range(60).map((m) => String(m).padStart(2, '0'))}
                    onChange={(data) => {
                      const newDate = endTriggerDate || new Date()
                      newDate.setMinutes(parseInt(data), 0)

                      setEndTriggerDate(newDate)
                      setEndTriggerTime(
                        `${endTriggerTime.split(':')[0]}:${data}`,
                      )
                    }}
                  />
                </Stack>
              </>
            )}
          </>
        )}
      </>
    )
  },
)
